import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Bookmark, BookmarkPosition } from 'entities/bookmark';
import { UserContext } from 'context-providers/user-provider';
import { apiBookmarkGet } from './get';
import { apiBookmarkPut } from './put';
import { ProductCodes } from 'lib/utils/get-product-specific-code';

const bookmarkQueryKey = (productGroup: ProductCodes) => [
  'bookmark',
  productGroup,
];

// null if the user has not interacted with member experience yet.
// `enabled` lets a caller further gate the fetch (e.g. behind a feature
// flag) on top of the login check — defaults to true so existing callers
// are unaffected.
export const useGetBookmark = (productGroup: ProductCodes, enabled = true) => {
  const { userState } = useContext(UserContext);
  const isLoggedIn = Boolean(userState?.email);

  return useQuery({
    queryKey: bookmarkQueryKey(productGroup),
    queryFn: () => apiBookmarkGet(productGroup),
    // @ts-ignore
    initialData: undefined as Bookmark | null,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: isLoggedIn && enabled,
  });
};

// Upserts the member's furthest-reached position for a product group
export const useUpsertBookmark = (productGroup: ProductCodes) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lastVisited: BookmarkPosition) =>
      apiBookmarkPut(productGroup, lastVisited),
    onSuccess: (bookmark) => {
      queryClient.setQueryData(bookmarkQueryKey(productGroup), bookmark);
    },
  });
};
